import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Alert, AlertTitle } from '@mui/material';

// Constantes para intervalos y límites
const AUTO_REFRESH_INTERVAL = 300000; // 5 minutos (5 * 60 * 1000)
const MIN_INTERVAL = 60000;    // Mínimo 1 minuto
const MAX_INTERVAL = 900000;   // Máximo 15 minutos
const MAX_CONSECUTIVE_AI_RESPONSES = 5;
const MAX_RETRIES = 3;

const NEGOTIATION_END_PHRASES = [
  'trato cerrado',
  'negociación finalizada',
  'acuerdo alcanzado',
  'acepto la oferta',
  'confirmo el precio',
  'trato hecho',
  'precio aceptado',
  'cerramos el trato',
  'acepto los términos',
  'estoy de acuerdo con'
];

const AutoNegotiationManager = ({
  isActive,
  chatId,
  lastMessageTimestamp,
  onNewMessage,
  onNegotiationEnd,
  chatService
}) => {
  const [lastCheck, setLastCheck] = useState(lastMessageTimestamp);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [consecutiveAIResponses, setConsecutiveAIResponses] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  
  // Referencia para el intervalo
  const intervalRef = useRef(null);
  
  // Función para detectar fin de negociación
  const checkForNegotiationEnd = useCallback((message) => {
    const isEnd = NEGOTIATION_END_PHRASES.some(phrase => 
      message.toLowerCase().includes(phrase.toLowerCase())
    );

    if (isEnd && onNegotiationEnd) {
      onNegotiationEnd({ 
        reason: 'agreement', 
        message,
        timestamp: new Date().toISOString()
      });
    }

    return isEnd;
  }, [onNegotiationEnd]);

  // Función para calcular el intervalo con límites
  const getInterval = useCallback(() => {
    const baseInterval = AUTO_REFRESH_INTERVAL;
    const errorMultiplier = Math.pow(2, retryCount);
    const calculatedInterval = baseInterval * errorMultiplier;
    
    return Math.min(Math.max(calculatedInterval, MIN_INTERVAL), MAX_INTERVAL);
  }, [retryCount]);

  // Función principal para verificar nuevos mensajes
  const checkNewMessages = useCallback(async () => {
    if (!isActive || !chatId || isChecking) return;
    
    try {
      setIsChecking(true);
      
      // Verificar estado de negociación
      const statusResponse = await chatService.checkNegotiationStatus(chatId);
      if (statusResponse.success && statusResponse.data?.isNegotiationEnded) {
        onNegotiationEnd?.({ 
          reason: 'statusCheck',
          status: statusResponse.data
        });
        return;
      }
      
      // Obtener mensajes nuevos
      const response = await chatService.getConversation(chatId);
      
      if (response.success && response.data) {
        setRetryCount(0);
        setLastError(null);

        const newMessages = response.data.filter(
          msg => new Date(msg.timestamp) > new Date(lastCheck)
        );
        
        // Separar mensajes por tipo
        const incomingMessages = newMessages.filter(msg => !msg.aiGenerated);
        const aiMessages = newMessages.filter(msg => msg.aiGenerated);
        
        // Manejar conteo de respuestas AI consecutivas
        if (aiMessages.length > 0) {
          setConsecutiveAIResponses(prev => {
            const newCount = prev + 1;
            if (newCount >= MAX_CONSECUTIVE_AI_RESPONSES) {
              onNegotiationEnd?.({ 
                reason: 'maxResponses', 
                count: newCount,
                timestamp: new Date().toISOString()
              });
            }
            return newCount;
          });
        } else {
          setConsecutiveAIResponses(0);
        }
        
        // Procesar mensajes entrantes
        if (incomingMessages.length > 0) {
          const lastMessage = incomingMessages[incomingMessages.length - 1];
          
          // Verificar si el mensaje indica fin de negociación
          if (!checkForNegotiationEnd(lastMessage.message) && 
              consecutiveAIResponses < MAX_CONSECUTIVE_AI_RESPONSES) {
            onNewMessage(lastMessage);
          }
          
          setLastCheck(lastMessage.timestamp);
        }
      }
    } catch (error) {
      console.error('[AutoNegotiationManager] Error checking messages:', error);
      
      // Manejar reintentos
      setRetryCount(prev => {
        const newCount = prev + 1;
        if (newCount >= MAX_RETRIES) {
          setError('Demasiados errores consecutivos. La negociación se detendrá.');
          onNegotiationEnd?.({ 
            reason: 'maxRetries', 
            error: error.message,
            retryCount: newCount,
            timestamp: new Date().toISOString()
          });
          return 0;
        }
        return newCount;
      });

      setLastError(error);
      
      // Manejar errores específicos
      if (error.message?.includes('API limit') || error.response?.status === 429) {
        setError('Límite de API alcanzado. La negociación se detendrá.');
        onNegotiationEnd?.({ 
          reason: 'apiLimit', 
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }
    } finally {
      setIsChecking(false);
    }
  }, [
    isActive, 
    chatId, 
    lastCheck, 
    isChecking, 
    consecutiveAIResponses,
    onNewMessage,
    onNegotiationEnd,
    checkForNegotiationEnd,
    chatService
  ]);

  // Configurar intervalo con backoff exponencial
  useEffect(() => {
    if (isActive) {
      const interval = getInterval();
      intervalRef.current = setInterval(checkNewMessages, interval);

      // Ejecutar verificación inmediata al activar
      checkNewMessages();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      // Limpiar estado al desactivar
      setLastCheck(lastMessageTimestamp);
      setError(null);
      setConsecutiveAIResponses(0);
      setRetryCount(0);
      setLastError(null);
    }
  }, [isActive, getInterval, checkNewMessages, lastMessageTimestamp]);

  // Actualizar lastCheck cuando cambia lastMessageTimestamp
  useEffect(() => {
    if (lastMessageTimestamp) {
      setLastCheck(lastMessageTimestamp);
    }
  }, [lastMessageTimestamp]);

  // Renderizar error si existe
  if (error) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Alert 
          severity="error"
          sx={{
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          <AlertTitle>Error en la negociación automática</AlertTitle>
          {error}
          {lastError && (
            <Box component="pre" sx={{ mt: 1, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
              Detalles: {lastError.message}
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  // El componente no renderiza nada por defecto
  return null;
};

export default AutoNegotiationManager;